import { Stack } from "../../../stack";
import { Text } from "../../../text";
import { Group } from "../../../group";
import { InteractionIcon } from "../../../interaction-icon";
import { format, formatDistanceToNow } from "date-fns";
import styles from "./sidebar-content.module.scss";
import classnames from "classnames";
import { useInteractionModalContext } from "../context/context";
import { Skeleton } from "../../../skeleton";
import { Icon } from "../../../icon";
import { faClockRotateLeft } from "@fortawesome/pro-regular-svg-icons";
import { ActionIcon } from "../../../action-icon";
import { useScrollPositionPreservation } from "../hooks";
import {
    ISidebarFetchMoreItemsProps,
    ISidebarItemProps,
    ISidebarItemWrapperProps,
    SidebarItemType,
} from "./sidebar-content.types";
import { usePrevious } from "@mantine/hooks";
import { kebabCase } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "../../../box";

const SidebarItemWrapper = ({ children, onClick, active, "data-testid": dataTestId }: ISidebarItemWrapperProps) => {
    return (
        <Group
            wrap="nowrap"
            flex="1 0 auto"
            gap="sm"
            p="sm"
            w="100%"
            justify="flex-start"
            data-active={active}
            className={classnames(styles["sidebar-content-item"], "global-menu-item")}
            onClick={onClick}
            data-testid={dataTestId}
        >
            {children}
        </Group>
    );
};

const Item = ({ interactionType, dateTime, onClick, active }: ISidebarItemProps) => {
    return (
        <SidebarItemWrapper
            onClick={onClick}
            active={active}
            data-testid={`interaction-sidebar-item-${kebabCase(interactionType)}`}
        >
            <InteractionIcon size="md" type={interactionType} />
            <Stack gap="0" data-hide-on-sidebar-collapse>
                <Text noWrap size="sm" data-testid="interaction-sidebar-item-date">
                    {format(dateTime, "d LLL, h:mmaaa")}
                </Text>
                <Text noWrap variant="medium" size="xs" dimmed>
                    {formatDistanceToNow(dateTime)} ago
                </Text>
            </Stack>
        </SidebarItemWrapper>
    );
};

const ItemSkeleton = () => <Skeleton height={44} />;
Item.Skeleton = ItemSkeleton;

const FetchMoreItems = ({ onClick, label, type, isLoading }: ISidebarFetchMoreItemsProps) => {
    return (
        <SidebarItemWrapper
            onClick={isLoading ? undefined : onClick}
            data-testid={`fetch-more-interactions-button-${kebabCase(label)}`}
        >
            <ActionIcon loading={isLoading} bdrs="xxl" color="gray" variant="default" size="sm">
                <Icon flip={type === "newer" ? "horizontal" : undefined} icon={faClockRotateLeft} />
            </ActionIcon>
            <Text noWrap data-hide-on-sidebar-collapse size="sm">
                {label}
            </Text>
        </SidebarItemWrapper>
    );
};

export const SidebarContent = () => {
    const { interaction, interactionsFromSameContact, interactionsFromSameContactNavigation } =
        useInteractionModalContext();

    const previousItemsList = usePrevious(interactionsFromSameContact?.data);
    const activeItemRef = useRef<HTMLDivElement>(null);
    const [shouldScrollToItem, setShouldScrollToItem] = useState(true);
    const isLoading = useMemo(
        () => interactionsFromSameContact?.isLoading || interaction.isLoading,
        [interactionsFromSameContact, interaction]
    );

    const [scrollRef, { enable: enableScrollPreservation }] = useScrollPositionPreservation(
        interactionsFromSameContact?.data?.length !== previousItemsList?.length
    );

    // Auto-scroll to active item when the data loads
    useEffect(() => {
        if (shouldScrollToItem && !isLoading && interaction.data && activeItemRef.current) {
            activeItemRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            setShouldScrollToItem(false);
        }
    }, [shouldScrollToItem, interaction.data, isLoading]);

    useEffect(() => {
        if (interactionsFromSameContact?.isLoading) {
            setShouldScrollToItem(true);
        }
    }, [interactionsFromSameContact?.isLoading]);

    if (typeof interactionsFromSameContact === "undefined") {
        return null;
    }

    return (
        <Stack ref={scrollRef} className={styles["sidebar-content"]} align="flex-start" justify="flex-start">
            <Stack w="100%" align="flex-start" justify="flex-start">
                {interactionsFromSameContact.isLoading && (
                    <>
                        <Item.Skeleton />
                        <Item.Skeleton />
                        <Item.Skeleton />
                        <Item.Skeleton />
                        <Item.Skeleton />
                        <Item.Skeleton />
                    </>
                )}
                {interactionsFromSameContact.hasPreviousPage && !interactionsFromSameContact.isLoading && (
                    <FetchMoreItems
                        label="See Older"
                        type={SidebarItemType.Older}
                        onClick={() => {
                            enableScrollPreservation();
                            interactionsFromSameContact?.fetchPreviousPage?.();
                        }}
                        isLoading={interactionsFromSameContact.isLoadingPreviousPage}
                    />
                )}
                {!interactionsFromSameContact.isLoading &&
                    interactionsFromSameContact.data?.map((item) => {
                        const isActive = interaction.data?.interactionId === item.interactionId;
                        return (
                            <Box key={item.interactionId} ref={isActive ? activeItemRef : null} w="100%">
                                <Item
                                    onClick={() => interactionsFromSameContactNavigation?.onClick(item.interactionId)}
                                    active={isActive}
                                    interactionType={item.interactionType}
                                    dateTime={item.startDateTime}
                                />
                            </Box>
                        );
                    })}
                {interactionsFromSameContact.hasNextPage && !interactionsFromSameContact.isLoading && (
                    <FetchMoreItems
                        label="See Newer"
                        type={SidebarItemType.Newer}
                        onClick={interactionsFromSameContact.fetchNextPage}
                        isLoading={interactionsFromSameContact.isLoadingNextPage}
                    />
                )}
            </Stack>
        </Stack>
    );
};
